/**
 * EconQuest Phase 2 — Content Ingestion Contract (single source of truth).
 *
 * This Zod schema is the contract shared by:
 *   - the LLM extraction pass (Stage 2) — its required output shape,
 *   - the validation gates (Gates 1-3),
 *   - the DRAFT loader (Stage 4) — what it maps into question_templates / scenario_pools.
 *
 * It mirrors the relevant columns of question_templates (DDL Spec §3.10) and the
 * seed_body JSON shape from EconQuest_Phase2_Ingestion_Plan_v1.0.md §4.2.
 *
 * NOTE: enum string-literal unions below are intentionally re-declared (not imported
 * from ./enums) so this file stays a self-contained contract; a unit test asserts they
 * stay in sync with the canonical BATTLE_TYPE / SCENARIO_POOL_T arrays.
 */
import { z } from 'zod';
/** Edexcel command words we recognise in the revision-guide docs. */
export const COMMAND_WORDS = [
    'MCQ',
    'Define',
    'State',
    'Calculate',
    'Draw',
    'Explain',
    'Analyse',
    'Assess',
    'Discuss',
    'Evaluate',
];
/** battle_type enum mirror (must match shared BATTLE_TYPE). */
export const INGEST_BATTLE_TYPE = [
    'DEFINITION',
    'EXPLAIN',
    'MINIBOSS',
    'STAGEBOSS',
    'FINALBOSS',
];
/** scenario_pools.pool_type enum mirror (must match shared SCENARIO_POOL_T). */
export const INGEST_SCENARIO_POOL_T = [
    'COUNTRY',
    'FIRM',
    'PRODUCT',
    'FIGURE_RULE',
];
/** A single cluster letter A..Q. */
export const clusterLetterSchema = z
    .string()
    .length(1)
    .regex(/^[A-Q]$/, 'cluster letter must be A..Q');
/** AO weights must sum to 1.0 (checked in Gate 2, not here, to keep messages clear). */
export const aoWeightsSchema = z
    .object({
    AO1: z.number().min(0).max(1).optional(),
    AO2: z.number().min(0).max(1).optional(),
    AO3: z.number().min(0).max(1).optional(),
    AO4: z.number().min(0).max(1).optional(),
})
    .strict();
/** Points-based mark scheme entry. */
export const markPointSchema = z
    .object({
    marks: z.number().int().min(1),
    criterion: z.string().min(1),
})
    .strict();
/** Levels-based mark scheme entry (marks is a string range like "5-6"). */
export const markLevelSchema = z
    .object({
    level: z.number().int().min(1),
    marks: z.string().min(1),
    descriptor: z.string().min(1),
})
    .strict();
export const markSchemeSchema = z
    .object({
    type: z.enum(['points', 'levels']),
    points: z.array(markPointSchema).optional(),
    levels: z.array(markLevelSchema).optional(),
    indicative_content: z.array(z.string().min(1)).optional(),
})
    .strict()
    .refine((m) => (m.type === 'points' ? !!m.points?.length : !!m.levels?.length), { message: "mark_scheme.type 'points' requires points[]; 'levels' requires levels[]" });
/** MCQ option. Exactly one is_correct:true enforced in Gate 2. */
export const distractorSchema = z
    .object({
    option: z.string().min(1), // "A".."D"
    text: z.string().min(1),
    is_correct: z.boolean(),
    why_wrong: z.string().optional(), // required for wrong options (Gate 2)
})
    .strict();
/** Provenance — mandatory on every extracted row for traceability + re-ingestion. */
export const provenanceSchema = z
    .object({
    doc: z.string().min(1),
    section: z.string().min(1),
    line_start: z.number().int().min(1).optional(),
})
    .strict();
/** seed_body — the structured question payload stored as jsonb on question_templates. */
export const seedBodySchema = z
    .object({
    stem: z.string().min(1),
    command_word: z.enum(COMMAND_WORDS),
    marks: z.number().int().min(1).max(12),
    mark_scheme: markSchemeSchema,
    model_answer: z.string().optional(),
    distractors: z.array(distractorSchema).optional(),
    source: provenanceSchema,
    /**
     * Provenance metadata mirrored into seed_body (jsonb) so it persists without a DB
     * migration and is queryable via seed_body->>'source_quality'. Mirrors the top-level
     * extractedTemplate fields of the same name. Optional so older rows stay valid.
     */
    source_quality: z.enum(['GENUINE', 'SYNTHETIC']).optional(),
    rubric_injected_levels: z.array(z.number().int().min(1)).optional(),
    /**
     * Phase 3 authoring (portal/boss content). The undirected portal identity this
     * authored question belongs to, e.g. 'A-N' or path-edge 'D-E' (sorted endpoint pair
     * joined by '-'), plus its tier. Recorded here — not only in portal_clusters — because
     * path6 questions MUST keep portal_clusters=[] to stay Gate-2-legal (a path edge is a
     * PREREQS progression edge, not a DAG portal edge, so it would fail Gate 2's
     * "portal edge present in DAG" check). conditional/final questions set portal_clusters
     * normally AND stamp these for queryability. Optional so all pre-P3 rows stay valid.
     */
    portal_key: z.string().optional(),
    tier: z.enum(['path6', 'conditional', 'final']).optional(),
})
    .strict();
/**
 * source_quality — provenance quality flag for SME review prioritisation.
 *   GENUINE   — the question + mark scheme were fully present in the source doc.
 *   SYNTHETIC — the source self-declares the item as illustrative/simulated (e.g. a
 *               12-mark question that "does not appear as a standalone cluster question"),
 *               OR the platform injected a canonical Edexcel level rubric because the
 *               source only described the top (Level 3) band. Still loads as DRAFT.
 */
export const SOURCE_QUALITY = ['GENUINE', 'SYNTHETIC'];
/**
 * The full extracted candidate row. Maps to question_templates columns plus seed_body.
 * concept_tags / portal_clusters are validated against clusters + DAG in Gate 2.
 */
export const extractedTemplateSchema = z
    .object({
    cluster_id: clusterLetterSchema,
    battle_type: z.enum(INGEST_BATTLE_TYPE),
    command_word: z.enum(COMMAND_WORDS),
    mark_value: z.number().int().min(1).max(12),
    sub_concept: z.string().optional(), // e.g. "A2 Opportunity Cost"
    ao_weights: aoWeightsSchema,
    concept_tags: z.array(z.string()).default([]),
    portal_clusters: z.array(clusterLetterSchema).default([]),
    paper: z.union([z.literal(1), z.literal(2)]),
    scenario_type: z.string().optional(),
    data_required: z.boolean().default(false),
    examiner_traps: z.array(z.string()).default([]),
    source_quality: z.enum(SOURCE_QUALITY).default('GENUINE'),
    /**
     * Marks indices (1-based) of the levels[] bands whose descriptor text was injected
     * from the canonical Edexcel rubric rather than read from source. Gate 3 excludes
     * these descriptors from faithfulness checking. Empty for points-based / fully-sourced.
     */
    rubric_injected_levels: z.array(z.number().int().min(1)).default([]),
    seed_body: seedBodySchema,
})
    .strict();
/** A scenario candidate destined for scenario_pools. */
export const extractedScenarioSchema = z
    .object({
    cluster_id: clusterLetterSchema,
    pool_type: z.enum(INGEST_SCENARIO_POOL_T),
    value: z.string().min(1),
})
    .strict();
/** A proposed registry enrichment diff (R0: enrich-only, human-approved). */
export const registryEnrichmentSchema = z
    .object({
    cluster_id: clusterLetterSchema,
    term: z.string().min(1),
    add_examiner_traps: z.array(z.string()).default([]),
    fill_full_mark_example: z.string().optional(),
    fill_partial_example: z.string().optional(),
})
    .strict();
/** The complete per-cluster extraction output (before gates). */
export const clusterExtractionSchema = z
    .object({
    cluster_id: clusterLetterSchema,
    templates: z.array(extractedTemplateSchema),
    scenarios: z.array(extractedScenarioSchema).default([]),
    registry_enrichments: z.array(registryEnrichmentSchema).default([]),
})
    .strict();
/** Default AO-weight rule (Plan §3.2 / decision D2) — applied by command word. */
export function defaultAoWeights(cmd) {
    switch (cmd) {
        case 'MCQ':
        case 'Define':
        case 'State':
            return { AO1: 1.0 };
        case 'Calculate':
            // Quantitative skill: recall of method (AO1) applied to numeric data (AO2).
            return { AO1: 0.5, AO2: 0.5 };
        case 'Draw':
        case 'Explain':
            return { AO1: 0.4, AO2: 0.6 };
        case 'Analyse':
            return { AO2: 1.0 };
        case 'Assess':
        case 'Discuss':
        case 'Evaluate':
            // Extended-response level-marked questions: analysis + evaluation + judgement.
            return { AO2: 0.5, AO3: 0.25, AO4: 0.25 };
    }
}
const CANONICAL_LEVEL_RUBRICS = {
    12: [
        {
            level: 1,
            marks: '1-4',
            descriptor: 'Isolated/undeveloped knowledge. A few relevant points stated without development; little or no application to the context and no evaluation. Opportunity-cost or trade-off reasoning absent or merely asserted.',
        },
        {
            level: 2,
            marks: '5-8',
            descriptor: 'Developed analysis. Relevant points are explained with a clear chain of reasoning and applied to the context; benefits and/or costs are developed, but evaluation is limited or unbalanced and any judgement is unsupported.',
        },
        {
            level: 3,
            marks: '9-12',
            descriptor: 'Sustained, balanced evaluation. Well-developed analysis applied to the context, explicit weighing of costs against benefits (including the opportunity cost/trade-off), leading to a reasoned, supported judgement with appropriate qualification.',
        },
    ],
    9: [
        {
            level: 1,
            marks: '1-3',
            descriptor: 'Isolated/undeveloped knowledge. Relevant points stated with little development and limited application to the context; no effective evaluation.',
        },
        {
            level: 2,
            marks: '4-6',
            descriptor: 'Developed analysis applied to the context with a clear chain of reasoning; some evaluation attempted but limited, unbalanced, or with an unsupported judgement.',
        },
        {
            level: 3,
            marks: '7-9',
            descriptor: 'Well-developed, applied analysis with balanced evaluation and a reasoned, supported judgement.',
        },
    ],
    6: [
        {
            level: 1,
            marks: '1-2',
            descriptor: 'Basic knowledge. Relevant point(s) identified with little or no development; generic and not applied to the context.',
        },
        {
            level: 2,
            marks: '3-4',
            descriptor: 'Some developed analysis. A relevant point is explained with a partial chain of reasoning and some application to the context.',
        },
        {
            level: 3,
            marks: '5-6',
            descriptor: 'Clear, fully developed analysis with a complete chain of reasoning applied specifically to the context.',
        },
    ],
};
/**
 * Return the canonical Edexcel level rubric for a given mark value, or undefined if that
 * mark value is not levels-marked (then the row stays points-based). Returns deep copies
 * so callers can safely mutate.
 */
export function levelRubricFor(marks) {
    const rubric = CANONICAL_LEVEL_RUBRICS[marks];
    return rubric ? rubric.map((l) => ({ ...l })) : undefined;
}
/** battle_type mapping by command word (Plan §3.1 / decision D1 default). */
export function defaultBattleType(cmd) {
    switch (cmd) {
        case 'MCQ':
        case 'Define':
        case 'State':
        case 'Calculate':
            // Short, deterministic, points-marked recall/application battle.
            return 'DEFINITION';
        case 'Draw':
        case 'Explain':
        case 'Analyse':
        case 'Assess':
        case 'Discuss':
        case 'Evaluate':
            return 'EXPLAIN';
    }
}
//# sourceMappingURL=ingest-contract.js.map